import { getToken, clearToken } from '../utils/authToken';
import { isOwnerAdminEmail } from '../utils/isOwnerAdmin';
/**
 * Módulo Administrativo de Gestión de Usuarios (GAU)
 * Panel de administración para gestionar usuarios de DamyxIA
 * Solo accesible por administradores
 */

import { useState, useEffect, useCallback } from 'react';
import { API_URL, API } from '../config';
import { createPortal } from 'react-dom';
import {
  Users, Search, Filter, RefreshCw, UserX, UserCheck, Trash2,
  Key, Mail, Shield, ShieldAlert, ShieldCheck, X, Loader2,
  ChevronLeft, ChevronRight, AlertTriangle, CheckCircle, XCircle,
  Eye, Calendar, MessageSquare, Brain, Activity, Clock
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';


// Configuración de estados de usuario
const USER_STATUS_CONFIG = {
  active: {
    icon: ShieldCheck,
    color: 'text-green-400',
    bg: 'bg-green-500/20',
    label: { es: 'Activo', en: 'Active' }
  },
  suspended: {
    icon: ShieldAlert,
    color: 'text-red-400',
    bg: 'bg-red-500/20',
    label: { es: 'Suspendido', en: 'Suspended' }
  }
};

const AdminUserManagement = ({ isOpen, onClose }) => {
  const { language } = useLanguage();
  
  // Estados principales
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Paginación
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 15;
  
  // Filtros
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [verifiedFilter, setVerifiedFilter] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  
  // Modales de acción
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionModal, setActionModal] = useState(null); // 'reset', 'suspend', 'reactivate', 'delete', 'detail'
  const [actionLoading, setActionLoading] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [resetMethod, setResetMethod] = useState('email'); // 'email' o 'temp'
  const [tempPassword, setTempPassword] = useState('');
  const [userDetail, setUserDetail] = useState(null);
  
  // Textos i18n
  const txt = {
    es: {
      title: 'Administración de Usuarios',
      subtitle: 'Gestión completa de usuarios de DamyxIA',
      noAccess: 'Acceso Denegado',
      noAccessDesc: 'No tienes permisos de administrador para acceder a este panel.',
      search: 'Buscar por email o nombre...',
      filters: 'Filtros',
      all: 'Todos',
      active: 'Activos',
      suspended: 'Suspendidos',
      verified: 'Verificados',
      unverified: 'Sin verificar',
      users: 'usuarios',
      page: 'Página',
      of: 'de',
      actions: 'Acciones',
      resetPassword: 'Resetear Contraseña',
      suspend: 'Suspender',
      reactivate: 'Reactivar',
      delete: 'Eliminar',
      viewDetail: 'Ver Detalle',
      confirmAction: 'Confirmar Acción',
      cancel: 'Cancelar',
      confirm: 'Confirmar',
      resetPasswordTitle: 'Resetear Contraseña',
      resetPasswordDesc: 'Elige cómo resetear la contraseña del usuario:',
      sendEmail: 'Enviar email de recuperación',
      generateTemp: 'Generar contraseña temporal',
      suspendTitle: 'Suspender Usuario',
      suspendDesc: '¿Estás seguro de suspender a este usuario? No podrá acceder a su cuenta.',
      suspendReason: 'Motivo de la suspensión (opcional)',
      reactivateTitle: 'Reactivar Usuario',
      reactivateDesc: '¿Estás seguro de reactivar a este usuario? Podrá acceder nuevamente.',
      deleteTitle: 'Eliminar Usuario',
      deleteDesc: '¡ATENCIÓN! Esta acción es PERMANENTE. Se eliminarán todos los datos del usuario:',
      deleteWarning: 'Esta acción no se puede deshacer.',
      conversations: 'conversaciones',
      memories: 'memorias',
      userDetail: 'Detalle del Usuario',
      memberSince: 'Miembro desde',
      lastLogin: 'Último acceso',
      referralCode: 'Código de referido',
      referrals: 'Referidos',
      adminLogs: 'Historial de Admin',
      noLogs: 'Sin acciones registradas',
      tempPasswordGenerated: 'Contraseña temporal generada',
      copyPassword: 'Copiar contraseña',
      shareSafely: 'Compártela de forma segura con el usuario',
      // Stats
      totalUsers: 'Total Usuarios',
      verifiedUsers: 'Verificados',
      suspendedUsers: 'Suspendidos',
      activeWeek: 'Activos (7 días)',
      newWeek: 'Nuevos (7 días)',
      loading: 'Cargando...'
    },
    en: {
      title: 'User Administration',
      subtitle: 'Complete DamyxIA user management',
      noAccess: 'Access Denied',
      noAccessDesc: 'You don\'t have administrator permissions to access this panel.',
      search: 'Search by email or name...',
      filters: 'Filters',
      all: 'All',
      active: 'Active',
      suspended: 'Suspended',
      verified: 'Verified',
      unverified: 'Unverified',
      users: 'users',
      page: 'Page',
      of: 'of',
      actions: 'Actions',
      resetPassword: 'Reset Password',
      suspend: 'Suspend',
      reactivate: 'Reactivate',
      delete: 'Delete',
      viewDetail: 'View Detail',
      confirmAction: 'Confirm Action',
      cancel: 'Cancel',
      confirm: 'Confirm',
      resetPasswordTitle: 'Reset Password',
      resetPasswordDesc: 'Choose how to reset the user\'s password:',
      sendEmail: 'Send recovery email',
      generateTemp: 'Generate temporary password',
      suspendTitle: 'Suspend User',
      suspendDesc: 'Are you sure you want to suspend this user? They won\'t be able to access their account.',
      suspendReason: 'Suspension reason (optional)',
      reactivateTitle: 'Reactivate User',
      reactivateDesc: 'Are you sure you want to reactivate this user? They will be able to access again.',
      deleteTitle: 'Delete User',
      deleteDesc: 'WARNING! This action is PERMANENT. All user data will be deleted:',
      deleteWarning: 'This action cannot be undone.',
      conversations: 'conversations',
      memories: 'memories',
      userDetail: 'User Detail',
      memberSince: 'Member since',
      lastLogin: 'Last login',
      referralCode: 'Referral code',
      referrals: 'Referrals',
      adminLogs: 'Admin History',
      noLogs: 'No actions recorded',
      tempPasswordGenerated: 'Temporary password generated',
      copyPassword: 'Copy password',
      shareSafely: 'Share it safely with the user',
      totalUsers: 'Total Users',
      verifiedUsers: 'Verified',
      suspendedUsers: 'Suspended',
      activeWeek: 'Active (7 days)',
      newWeek: 'New (7 days)',
      loading: 'Loading...'
    }
  };
  
  const t = txt[language] || txt.es;
  
  // Obtener token
  const getToken = () => getToken();
  
  // Verificar si es admin y cargar datos
  const checkAdminAndLoadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = getToken();
      // CUADRO NEGRO FIX feb-2026 (capa 2): whitelist override para las 3
      // cuentas admin oficiales. Si el email del usuario actual está en la
      // whitelist, garantizamos isAdmin=true sin depender del token en memoria.
      const userEmail = (typeof window !== 'undefined' && localStorage.getItem('userEmail')) || '';
      const isOwnerAdmin = isOwnerAdminEmail(userEmail);

      const headers = {};  // P2.1 cookie-only via axios.defaults

      if (!isOwnerAdmin) {
        // Verificar permisos de admin contra el backend
        let checkResponse;
        try {
          checkResponse = await axios.get(`${API}/stats/check-admin`, {
            headers,
            withCredentials: true,
          });
        } catch (err) {
          if (err.response?.status === 401 || err.response?.status === 403) {
            console.warn('[Admin] Sesión expirada o sin permisos');
          } else {
            console.error('[Admin] Error inesperado en check-admin:', err);
          }
          setIsAdmin(false);
          setIsLoading(false);
          return;
        }

        if (!checkResponse.data?.is_admin) {
          setIsAdmin(false);
          setIsLoading(false);
          return;
        }
      }

      setIsAdmin(true);

      // Cargar estadísticas (best-effort: si falla, no rompe el panel)
      try {
        const statsResponse = await axios.get(`${API}/admin/stats/overview`, {
          headers,
          withCredentials: true,
        });
        setStats(statsResponse.data);
      } catch (err) {
        console.warn('[Admin] No se pudo cargar overview stats:', err?.message);
      }

      // Cargar usuarios
      await loadUsers();

    } catch (error) {
      console.error('Error checking admin:', error);
      // Si el email está en whitelist NO bloqueamos por error genérico
      const userEmail = (typeof window !== 'undefined' && localStorage.getItem('userEmail')) || '';
      if (!isOwnerAdminEmail(userEmail) && error.response?.status === 403) {
        setIsAdmin(false);
      }
    } finally {
      setIsLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Cargar usuarios
  const loadUsers = async () => {
    try {
      const token = getToken();
      const headers = {};  // P2.1 cookie-only via axios.defaults
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });
      
      if (searchQuery) params.append('search', searchQuery);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (verifiedFilter !== null) params.append('verified', verifiedFilter.toString());
      
      const response = await axios.get(`${API}/admin/users?${params}`, { headers });
      
      setUsers(response.data.users);
      setTotal(response.data.total);
      setTotalPages(response.data.pages);
      
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error(language === 'es' ? 'Error al cargar usuarios' : 'Error loading users');
    }
  };
  
  // Cargar detalle de usuario
  const loadUserDetail = async (email) => {
    try {
      const token = getToken();
      const headers = {};  // P2.1 cookie-only via axios.defaults
      
      const response = await axios.get(`${API}/admin/users/${encodeURIComponent(email)}`, { headers });
      setUserDetail(response.data);
      
    } catch (error) {
      console.error('Error loading user detail:', error);
      toast.error(language === 'es' ? 'Error al cargar detalle' : 'Error loading detail');
    }
  };
  
  // Efectos
  useEffect(() => {
    if (isOpen) {
      checkAdminAndLoadData();
    }
  }, [isOpen, checkAdminAndLoadData]);
  
  useEffect(() => {
    if (isAdmin) {
      loadUsers();
    }
  }, [page, searchQuery, statusFilter, verifiedFilter, isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Acciones
  const handleResetPassword = async () => {
    if (!selectedUser) return;
    setActionLoading(true);
    
    try {
      const token = getToken();
      const response = await axios.post(
        `${API}/admin/users/reset-password`,
        {
          user_email: selectedUser.email,
          send_email: resetMethod === 'email'
        },
        {}
      );
      
      if (resetMethod === 'temp' && response.data.temp_password) {
        setTempPassword(response.data.temp_password);
      } else {
        toast.success(response.data.message);
        closeActionModal();
      }
      
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'es' ? 'Error' : 'Error'));
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleSuspend = async () => {
    if (!selectedUser) return;
    setActionLoading(true);
    
    try {
      const token = getToken();
      await axios.post(
        `${API}/admin/users/suspend`,
        {
          user_email: selectedUser.email,
          reason: suspendReason || null
        },
        {}
      );
      
      toast.success(language === 'es' ? 'Usuario suspendido' : 'User suspended');
      closeActionModal();
      loadUsers();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'es' ? 'Error' : 'Error'));
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleReactivate = async () => {
    if (!selectedUser) return;
    setActionLoading(true);
    
    try {
      const token = getToken();
      await axios.post(
        `${API}/admin/users/reactivate`,
        { user_email: selectedUser.email },
        {}
      );
      
      toast.success(language === 'es' ? 'Usuario reactivado' : 'User reactivated');
      closeActionModal();
      loadUsers();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'es' ? 'Error' : 'Error'));
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleDelete = async () => {
    if (!selectedUser) return;
    setActionLoading(true);
    
    try {
      const token = getToken();
      await axios.delete(
        `${API}/admin/users/delete`,
        {
          data: { user_email: selectedUser.email, confirm: true },
        }
      );
      
      toast.success(language === 'es' ? 'Usuario eliminado' : 'User deleted');
      closeActionModal();
      loadUsers();
      
      // Actualizar stats
      const statsResponse = await axios.get(`${API}/admin/stats/overview`, {
      });
      setStats(statsResponse.data);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'es' ? 'Error' : 'Error'));
    } finally {
      setActionLoading(false);
    }
  };
  
  const openActionModal = (user, action) => {
    setSelectedUser(user);
    setActionModal(action);
    setTempPassword('');
    setSuspendReason('');
    setResetMethod('email');
    
    if (action === 'detail') {
      loadUserDetail(user.email);
    }
  };
  
  const closeActionModal = () => {
    setActionModal(null);
    setSelectedUser(null);
    setTempPassword('');
    setSuspendReason('');
    setUserDetail(null);
  };
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(language === 'es' ? 'Copiado' : 'Copied');
  };
  
  if (!isOpen) return null;
  
  // Render de acceso denegado
  if (!isLoading && !isAdmin) {
    return createPortal(
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
        <div className="relative w-full max-w-md bg-zinc-900 border border-zinc-700 rounded-xl p-8 text-center">
          <ShieldAlert className="w-16 h-16 mx-auto mb-4 text-red-400" />
          <h2 className="text-xl font-bold text-white mb-2">{t.noAccess}</h2>
          <p className="text-zinc-400 mb-6">{t.noAccessDesc}</p>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg"
          >
            {t.cancel}
          </button>
        </div>
      </div>,
      document.body
    );
  }
  
  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative w-full max-w-6xl bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-r from-red-600 to-orange-600 rounded-lg">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">{t.title}</h2>
              <p className="text-xs text-zinc-400">{t.subtitle}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg" data-testid="close-admin-panel">
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>
        
        {isLoading ? (
          <div className="flex-1 flex items-center justify-center py-20">
            <Loader2 className="w-10 h-10 animate-spin text-orange-500" />
          </div>
        ) : (
          <>
            {/* Stats Cards */}
            {stats && (
              <div className="px-6 py-4 border-b border-zinc-800">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <div className="bg-zinc-800/50 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Users className="w-4 h-4 text-blue-400" />
                      <span className="text-xs text-zinc-400">{t.totalUsers}</span>
                    </div>
                    <p className="text-xl font-bold text-white">{stats.users.total}</p>
                  </div>
                  <div className="bg-zinc-800/50 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span className="text-xs text-zinc-400">{t.verifiedUsers}</span>
                    </div>
                    <p className="text-xl font-bold text-white">{stats.users.verified}</p>
                  </div>
                  <div className="bg-zinc-800/50 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <XCircle className="w-4 h-4 text-red-400" />
                      <span className="text-xs text-zinc-400">{t.suspendedUsers}</span>
                    </div>
                    <p className="text-xl font-bold text-white">{stats.users.suspended}</p>
                  </div>
                  <div className="bg-zinc-800/50 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Activity className="w-4 h-4 text-cyan-400" />
                      <span className="text-xs text-zinc-400">{t.activeWeek}</span>
                    </div>
                    <p className="text-xl font-bold text-white">{stats.users.active_last_week}</p>
                  </div>
                  <div className="bg-zinc-800/50 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Users className="w-4 h-4 text-violet-400" />
                      <span className="text-xs text-zinc-400">{t.newWeek}</span>
                    </div>
                    <p className="text-xl font-bold text-white">{stats.users.new_this_week}</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Search and Filters */}
            <div className="px-6 py-3 border-b border-zinc-800">
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                    placeholder={t.search}
                    data-testid="search-users"
                    className="w-full pl-10 pr-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  data-testid="btn-toggle-filters"
                  className={`px-4 py-2 rounded-lg border transition-all flex items-center gap-2 ${
                    showFilters ? 'bg-orange-600 border-orange-500 text-white' : 'bg-zinc-800 border-zinc-700 text-zinc-400'
                  }`}
                >
                  <Filter className="w-4 h-4" />
                  {t.filters}
                </button>
                <button
                  onClick={() => { loadUsers(); }}
                  className="px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-400 hover:text-white"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>
              
              {showFilters && (
                <div className="flex flex-wrap gap-2 mt-3">
                  <div className="flex gap-1">
                    {['all', 'active', 'suspended'].map(status => (
                      <button
                        key={status}
                        onClick={() => { setStatusFilter(status); setPage(1); }}
                        data-testid={`filter-status-${status}`}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                          statusFilter === status
                            ? 'bg-orange-600 text-white'
                            : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                        }`}
                      >
                        {t[status]}
                      </button>
                    ))}
                  </div>
                  <div className="w-px h-6 bg-zinc-700 self-center" />
                  <div className="flex gap-1">
                    <button
                      onClick={() => { setVerifiedFilter(null); setPage(1); }}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                        verifiedFilter === null ? 'bg-orange-600 text-white' : 'bg-zinc-800 text-zinc-400'
                      }`}
                    >
                      {t.all}
                    </button>
                    <button
                      onClick={() => { setVerifiedFilter(true); setPage(1); }}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                        verifiedFilter === true ? 'bg-green-600 text-white' : 'bg-zinc-800 text-zinc-400'
                      }`}
                    >
                      {t.verified}
                    </button>
                    <button
                      onClick={() => { setVerifiedFilter(false); setPage(1); }}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                        verifiedFilter === false ? 'bg-yellow-600 text-white' : 'bg-zinc-800 text-zinc-400'
                      }`}
                    >
                      {t.unverified}
                    </button>
                  </div>
                </div>
              )}
            </div>
            
            {/* Users Table */}
            <div className="flex-1 overflow-auto px-6 py-4">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-xs text-zinc-500 uppercase">
                    <th className="pb-3 font-medium">Usuario</th>
                    <th className="pb-3 font-medium">Estado</th>
                    <th className="pb-3 font-medium">Verificado</th>
                    <th className="pb-3 font-medium">Registro</th>
                    <th className="pb-3 font-medium">Último acceso</th>
                    <th className="pb-3 font-medium text-right">{t.actions}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800">
                  {users.map(user => {
                    const statusConfig = USER_STATUS_CONFIG[user.status] || USER_STATUS_CONFIG.active;
                    const StatusIcon = statusConfig.icon;
                    
                    return (
                      <tr key={user.id} className="hover:bg-zinc-800/30" data-testid={`user-row-${user.id}`}>
                        <td className="py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center text-white font-medium text-sm">
                              {user.name?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-white">{user.name}</p>
                              <p className="text-xs text-zinc-500">{user.email}</p>
                            </div>
                            {user.is_admin && (
                              <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 text-xs rounded-full">
                                Admin
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-3">
                          <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs ${statusConfig.bg} ${statusConfig.color}`}>
                            <StatusIcon className="w-3 h-3" />
                            {statusConfig.label[language]}
                          </span>
                        </td>
                        <td className="py-3">
                          {user.is_verified ? (
                            <CheckCircle className="w-5 h-5 text-green-400" />
                          ) : (
                            <XCircle className="w-5 h-5 text-zinc-600" />
                          )}
                        </td>
                        <td className="py-3 text-sm text-zinc-400">
                          {user.created_at ? new Date(user.created_at).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US') : '-'}
                        </td>
                        <td className="py-3 text-sm text-zinc-400">
                          {user.last_login ? new Date(user.last_login).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US') : '-'}
                        </td>
                        <td className="py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => openActionModal(user, 'detail')}
                              className="p-1.5 text-zinc-500 hover:text-blue-400 rounded"
                              title={t.viewDetail}
                              data-testid={`btn-detail-${user.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => openActionModal(user, 'reset')}
                              className="p-1.5 text-zinc-500 hover:text-yellow-400 rounded"
                              title={t.resetPassword}
                              data-testid={`btn-reset-${user.id}`}
                            >
                              <Key className="w-4 h-4" />
                            </button>
                            {user.status !== 'suspended' && !user.is_admin && (
                              <button
                                onClick={() => openActionModal(user, 'suspend')}
                                className="p-1.5 text-zinc-500 hover:text-orange-400 rounded"
                                title={t.suspend}
                                data-testid={`btn-suspend-${user.id}`}
                              >
                                <UserX className="w-4 h-4" />
                              </button>
                            )}
                            {user.status === 'suspended' && (
                              <button
                                onClick={() => openActionModal(user, 'reactivate')}
                                className="p-1.5 text-zinc-500 hover:text-green-400 rounded"
                                title={t.reactivate}
                                data-testid={`btn-reactivate-${user.id}`}
                              >
                                <UserCheck className="w-4 h-4" />
                              </button>
                            )}
                            {!user.is_admin && (
                              <button
                                onClick={() => openActionModal(user, 'delete')}
                                className="p-1.5 text-zinc-500 hover:text-red-400 rounded"
                                title={t.delete}
                                data-testid={`btn-delete-${user.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              
              {users.length === 0 && (
                <div className="text-center py-12 text-zinc-500">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>{language === 'es' ? 'No se encontraron usuarios' : 'No users found'}</p>
                </div>
              )}
            </div>
            
            {/* Pagination */}
            <div className="px-6 py-3 border-t border-zinc-800 flex items-center justify-between">
              <p className="text-sm text-zinc-500">
                {total} {t.users}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-2 rounded-lg bg-zinc-800 text-zinc-400 disabled:opacity-50"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-zinc-400">
                  {t.page} {page} {t.of} {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-2 rounded-lg bg-zinc-800 text-zinc-400 disabled:opacity-50"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* Action Modals */}
      {actionModal && selectedUser && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={closeActionModal} />
          
          <div className="relative w-full max-w-md bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl">
            <div className="p-6">
              {/* Reset Password Modal */}
              {actionModal === 'reset' && (
                <>
                  {tempPassword ? (
                    <>
                      <div className="text-center mb-4">
                        <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-400" />
                        <h3 className="text-lg font-semibold text-white">{t.tempPasswordGenerated}</h3>
                      </div>
                      <div className="bg-zinc-800 rounded-lg p-4 mb-4">
                        <p className="text-xs text-zinc-500 mb-2">Contraseña temporal:</p>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 text-lg font-mono text-green-400">{tempPassword}</code>
                          <button
                            onClick={() => copyToClipboard(tempPassword)}
                            className="p-2 bg-zinc-700 hover:bg-zinc-600 rounded"
                          >
                            <Mail className="w-4 h-4 text-zinc-300" />
                          </button>
                        </div>
                      </div>
                      <p className="text-sm text-yellow-400 mb-4">{t.shareSafely}</p>
                      <button
                        onClick={closeActionModal}
                        className="w-full py-2 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg"
                      >
                        {t.cancel}
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-3 mb-4">
                        <Key className="w-8 h-8 text-yellow-400" />
                        <div>
                          <h3 className="font-semibold text-white">{t.resetPasswordTitle}</h3>
                          <p className="text-sm text-zinc-400">{selectedUser.email}</p>
                        </div>
                      </div>
                      <p className="text-sm text-zinc-400 mb-4">{t.resetPasswordDesc}</p>
                      <div className="space-y-2 mb-6">
                        <label className="flex items-center gap-3 p-3 bg-zinc-800 rounded-lg cursor-pointer hover:bg-zinc-750">
                          <input
                            type="radio"
                            name="resetMethod"
                            value="email"
                            checked={resetMethod === 'email'}
                            onChange={() => setResetMethod('email')}
                            className="accent-orange-500"
                          />
                          <div>
                            <p className="text-sm text-white">{t.sendEmail}</p>
                          </div>
                        </label>
                        <label className="flex items-center gap-3 p-3 bg-zinc-800 rounded-lg cursor-pointer hover:bg-zinc-750">
                          <input
                            type="radio"
                            name="resetMethod"
                            value="temp"
                            checked={resetMethod === 'temp'}
                            onChange={() => setResetMethod('temp')}
                            className="accent-orange-500"
                          />
                          <div>
                            <p className="text-sm text-white">{t.generateTemp}</p>
                          </div>
                        </label>
                      </div>
                      <div className="flex gap-3">
                        <button onClick={closeActionModal} className="flex-1 py-2 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg">
                          {t.cancel}
                        </button>
                        <button
                          onClick={handleResetPassword}
                          disabled={actionLoading}
                          className="flex-1 py-2 bg-yellow-600 hover:bg-yellow-500 text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                          {actionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                          {t.confirm}
                        </button>
                      </div>
                    </>
                  )}
                </>
              )}
              
              {/* Suspend Modal */}
              {actionModal === 'suspend' && (
                <>
                  <div className="flex items-center gap-3 mb-4">
                    <UserX className="w-8 h-8 text-orange-400" />
                    <div>
                      <h3 className="font-semibold text-white">{t.suspendTitle}</h3>
                      <p className="text-sm text-zinc-400">{selectedUser.email}</p>
                    </div>
                  </div>
                  <p className="text-sm text-zinc-400 mb-4">{t.suspendDesc}</p>
                  <textarea
                    value={suspendReason}
                    onChange={(e) => setSuspendReason(e.target.value)}
                    placeholder={t.suspendReason}
                    className="w-full p-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 mb-4 resize-none"
                    rows={3}
                  />
                  <div className="flex gap-3">
                    <button onClick={closeActionModal} className="flex-1 py-2 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg">
                      {t.cancel}
                    </button>
                    <button
                      onClick={handleSuspend}
                      disabled={actionLoading}
                      className="flex-1 py-2 bg-orange-600 hover:bg-orange-500 text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {actionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                      {t.suspend}
                    </button>
                  </div>
                </>
              )}
              
              {/* Reactivate Modal */}
              {actionModal === 'reactivate' && (
                <>
                  <div className="flex items-center gap-3 mb-4">
                    <UserCheck className="w-8 h-8 text-green-400" />
                    <div>
                      <h3 className="font-semibold text-white">{t.reactivateTitle}</h3>
                      <p className="text-sm text-zinc-400">{selectedUser.email}</p>
                    </div>
                  </div>
                  <p className="text-sm text-zinc-400 mb-6">{t.reactivateDesc}</p>
                  <div className="flex gap-3">
                    <button onClick={closeActionModal} className="flex-1 py-2 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg">
                      {t.cancel}
                    </button>
                    <button
                      onClick={handleReactivate}
                      disabled={actionLoading}
                      className="flex-1 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {actionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                      {t.reactivate}
                    </button>
                  </div>
                </>
              )}
              
              {/* Delete Modal */}
              {actionModal === 'delete' && (
                <>
                  <div className="flex items-center gap-3 mb-4">
                    <AlertTriangle className="w-8 h-8 text-red-400" />
                    <div>
                      <h3 className="font-semibold text-white">{t.deleteTitle}</h3>
                      <p className="text-sm text-zinc-400">{selectedUser.email}</p>
                    </div>
                  </div>
                  <div className="bg-red-900/20 border border-red-800 rounded-lg p-4 mb-4">
                    <p className="text-sm text-red-300 mb-2">{t.deleteDesc}</p>
                    <ul className="text-sm text-red-400/70 space-y-1 ml-4 list-disc">
                      <li>{t.conversations}</li>
                      <li>{t.memories}</li>
                      <li>Perfil y configuración</li>
                    </ul>
                    <p className="text-sm text-red-400 font-medium mt-3">{t.deleteWarning}</p>
                  </div>
                  <div className="flex gap-3">
                    <button onClick={closeActionModal} className="flex-1 py-2 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg">
                      {t.cancel}
                    </button>
                    <button
                      onClick={handleDelete}
                      disabled={actionLoading}
                      className="flex-1 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {actionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                      {t.delete}
                    </button>
                  </div>
                </>
              )}
              
              {/* Detail Modal */}
              {actionModal === 'detail' && (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center text-white font-medium text-xl">
                        {selectedUser.name?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h3 className="font-semibold text-white">{selectedUser.name}</h3>
                        <p className="text-sm text-zinc-400">{selectedUser.email}</p>
                      </div>
                    </div>
                    <button onClick={closeActionModal} className="p-2 hover:bg-zinc-800 rounded-lg">
                      <X className="w-5 h-5 text-zinc-400" />
                    </button>
                  </div>
                  
                  {userDetail ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-zinc-800 rounded-lg p-3">
                          <div className="flex items-center gap-2 mb-1">
                            <Calendar className="w-4 h-4 text-zinc-500" />
                            <span className="text-xs text-zinc-500">{t.memberSince}</span>
                          </div>
                          <p className="text-sm text-white">
                            {userDetail.created_at ? new Date(userDetail.created_at).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US') : '-'}
                          </p>
                        </div>
                        <div className="bg-zinc-800 rounded-lg p-3">
                          <div className="flex items-center gap-2 mb-1">
                            <Clock className="w-4 h-4 text-zinc-500" />
                            <span className="text-xs text-zinc-500">{t.lastLogin}</span>
                          </div>
                          <p className="text-sm text-white">
                            {userDetail.last_login ? new Date(userDetail.last_login).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US') : '-'}
                          </p>
                        </div>
                        <div className="bg-zinc-800 rounded-lg p-3">
                          <div className="flex items-center gap-2 mb-1">
                            <MessageSquare className="w-4 h-4 text-zinc-500" />
                            <span className="text-xs text-zinc-500">{t.conversations}</span>
                          </div>
                          <p className="text-sm text-white">{userDetail.conversation_count}</p>
                        </div>
                        <div className="bg-zinc-800 rounded-lg p-3">
                          <div className="flex items-center gap-2 mb-1">
                            <Brain className="w-4 h-4 text-zinc-500" />
                            <span className="text-xs text-zinc-500">{t.memories}</span>
                          </div>
                          <p className="text-sm text-white">{userDetail.memory_count}</p>
                        </div>
                      </div>
                      
                      {userDetail.referral_code && (
                        <div className="bg-zinc-800 rounded-lg p-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="text-xs text-zinc-500">{t.referralCode}</span>
                              <p className="text-sm font-mono text-orange-400">{userDetail.referral_code}</p>
                            </div>
                            <div className="text-right">
                              <span className="text-xs text-zinc-500">{t.referrals}</span>
                              <p className="text-sm text-white">{userDetail.referral_count}</p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {userDetail.admin_logs && userDetail.admin_logs.length > 0 && (
                        <div>
                          <p className="text-sm text-zinc-400 mb-2">{t.adminLogs}</p>
                          <div className="bg-zinc-800 rounded-lg divide-y divide-zinc-700 max-h-40 overflow-auto">
                            {userDetail.admin_logs.map((log, i) => (
                              <div key={i} className="p-2 text-xs">
                                <div className="flex items-center justify-between">
                                  <span className="text-orange-400">{log.action}</span>
                                  <span className="text-zinc-500">
                                    {new Date(log.timestamp).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US')}
                                  </span>
                                </div>
                                <p className="text-zinc-500">by {log.admin_user}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

export default AdminUserManagement;
